import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, Card, Table, Typography, Button, Empty, message, theme } from 'antd';
import {
  ArrowLeftOutlined, FileExcelOutlined, FilePdfOutlined, BarChartOutlined,
} from '@ant-design/icons';
import PujaSpinner from '../../components/PujaSpinner';
import { analyticsService, eventoService } from '../../services/api';
import { descargarBlob } from '../../utils/descargas';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title, Text } = Typography;

// Debe coincidir con REPORTES en backend/app/routes/analytics_routes.py.
const TIPOS_VALIDOS = new Set([
  'visitas', 'compartidos', 'ofertas_animal', 'ofertas_categoria', 'ofertas_corral',
]);

// Columnas que se muestran alineadas a la derecha (todo lo numérico).
const COLS_NUMERICAS = new Set(['visitas', 'compartidos', 'ofertas', 'monto_maximo', 'total_grupo']);

// Las que se muestran con $. Debe coincidir con COLUMNAS_MONEDA del backend
// (analytics_routes.py).
const COLS_MONEDA = new Set(['monto_maximo', 'total_grupo']);

// Debe coincidir con COLUMNAS_FECHA del backend (analytics_routes.py): llegan
// formateadas DD-MM-YYYY, y ordenar ESO como texto ordena por DÍA — con una
// ventana de pre-ofertas que cruza de mes, "02-09" sale antes que "28-08" y la
// columna dice lo contrario de la verdad.
const COLS_FECHA = new Set(['fecha_ganadora']);

// DD-MM-YYYY → YYYYMMDD, que sí ordena cronológicamente como texto.
// `hora_ganadora` no lo necesita: HH:MM con cero a la izquierda ya ordena bien.
const fechaOrdenable = (v) => (v ? String(v).split('-').reverse().join('') : '');

const PAGE_SIZE_DEFAULT = 20;

const AnalyticsCompleto = () => {
  const { tipo } = useParams();
  const [searchParams] = useSearchParams();
  const eventoId = searchParams.get('evento');
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();

  const [loading, setLoading] = useState(true);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [reporte, setReporte] = useState(null);
  // Analytics.jsx ya tiene el nombre en memoria y lo pasa por router state
  // (evita otro round-trip). Si se entra por link directo/bookmark (sin
  // state), cae a lista-corta — nunca al getById completo con la config del
  // micrositio, que acá no hace falta para nada.
  const [nombreEvento, setNombreEvento] = useState(location.state?.nombreEvento || '');
  // Página actual controlada a mano: la necesitamos para que la columna "#"
  // numere 21, 22, 23... en la página 2 en vez de reiniciar en 1 (el índice
  // que antd pasa a `render` es relativo a la página, no al dataset).
  const [pagina, setPagina] = useState({ current: 1, pageSize: PAGE_SIZE_DEFAULT });

  const tipoValido = TIPOS_VALIDOS.has(tipo);

  const cargar = useCallback(async () => {
    // El componente no remonta al navegar entre /analytics/completo/:tipo
    // con distinto tipo (misma ruta, cambia el param) — sin este reset, la
    // página 3 de "visitas" queda pegada al entrar a "ofertas_categoria"
    // con 4 filas y la tabla se ve vacía aunque el fetch haya andado bien.
    setPagina((p) => ({ ...p, current: 1 }));
    if (!eventoId || !tipoValido) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const promesas = [analyticsService.getCompleto(eventoId, tipo)];
      if (!location.state?.nombreEvento) {
        promesas.push(eventoService.getListaCorta());
      }
      const [resReporte, resEventos] = await Promise.all(promesas);
      setReporte(resReporte.data);
      if (resEventos) {
        // Si la lista vino cortada y este remate quedo afuera, el nombre no
        // aparece: el aviso del helper explica por que.
        const ev = eventosDeListaCorta(resEventos).find((e) => String(e.id_evento) === String(eventoId));
        if (ev) setNombreEvento(ev.nombre_evento);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar la estadística completa');
      setReporte(null);
    } finally {
      setLoading(false);
    }
  }, [eventoId, tipo, tipoValido, location.state]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleExportarExcel = async () => {
    setExportandoExcel(true);
    try {
      const res = await analyticsService.exportarExcel(eventoId, tipo);
      descargarBlob(res, `${tipo}_${eventoId}.xlsx`);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al exportar a Excel');
    } finally {
      setExportandoExcel(false);
    }
  };

  const handleExportarPdf = async () => {
    setExportandoPdf(true);
    try {
      const res = await analyticsService.exportarPdf(eventoId, tipo);
      descargarBlob(res, `${tipo}_${eventoId}.pdf`);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al exportar a PDF');
    } finally {
      setExportandoPdf(false);
    }
  };

  const columnas = (reporte?.columnas || []).map((c) => ({
    title: c.label,
    dataIndex: c.key,
    key: c.key,
    align: COLS_NUMERICAS.has(c.key) ? 'right' : 'left',
    sorter: (a, b) => {
      const va = a[c.key];
      const vb = b[c.key];
      if (COLS_FECHA.has(c.key)) {
        return fechaOrdenable(va).localeCompare(fechaOrdenable(vb));
      }
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va || '').localeCompare(String(vb || ''));
    },
    render: (v, r) => {
      if (v === null || v === undefined || v === '') return '—';
      if (COLS_MONEDA.has(c.key)) return `$${Number(v).toLocaleString('es-AR')}`;
      // El renglón de un corral agrupado (reporte de visitas) va en negrita:
      // su `nom_animal` es "Corral 47 · 3 cabezas" y `visitas` la suma del lote.
      if (r.children && (c.key === 'nom_animal' || c.key === 'visitas')) return <Text strong>{v}</Text>;
      return v;
    },
  }));

  const columnasConIndice = [
    {
      title: '#',
      width: 50,
      // Los animales dentro de un corral agrupado (`nivel` 1) no se numeran:
      // el número es del renglón del corral.
      render: (_, r, i) => (r.nivel === 1 ? '' : (pagina.current - 1) * pagina.pageSize + i + 1),
    },
    ...columnas,
  ];

  // Cada tipo de reporte tiene un campo naturalmente único propio: usarlo de
  // rowKey (no el índice) para que sorter/paginación no desincronicen el
  // DOM de las filas al reordenar. `clave` la manda el reporte de visitas
  // ('a-<id>' / 'c-<corral>'): el renglón de un corral no tiene id_animal.
  const rowKey = (record, i) =>
    record.clave ?? record.id_animal ?? record.nom_categoria ?? record.corral ?? i;

  return (
    <div style={{ padding: '24px' }}>
      <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/analytics')}
            style={{ marginBottom: 8 }}
          >
            Volver a Analytics
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            <BarChartOutlined style={{ marginRight: 10, color: token.colorPrimary }} />
            {reporte?.titulo || 'Estadística completa'}
          </Title>
          {nombreEvento && (
            <Text type="secondary">{nombreEvento}</Text>
          )}
          {/* El selector de días del dashboard NO filtra estas tablas — ahí
              tampoco lo hace, así que el número siempre es histórico del
              evento completo. Lo aclaramos para que no se lea como un
              recorte de "los últimos N días" que se perdió en el camino. */}
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            Histórico completo del evento (no aplica el filtro de días del dashboard)
          </Text>
        </Col>
        <Col>
          <Row gutter={12}>
            <Col>
              <Button
                icon={<FileExcelOutlined />}
                loading={exportandoExcel}
                onClick={handleExportarExcel}
                disabled={!reporte?.filas?.length}
              >
                Exportar Excel
              </Button>
            </Col>
            <Col>
              <Button
                icon={<FilePdfOutlined />}
                loading={exportandoPdf}
                onClick={handleExportarPdf}
                disabled={!reporte?.filas?.length}
              >
                Exportar PDF
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      {loading ? (
        <PujaSpinner size="lg" />
      ) : !eventoId || !tipoValido ? (
        <Empty description={
          !eventoId
            ? 'Falta el evento — volvé a Analytics y entrá de nuevo desde ahí'
            : 'Tipo de reporte desconocido — volvé a Analytics y entrá de nuevo desde ahí'
        } />
      ) : (
        <Card variant="borderless" style={{ borderRadius: token.borderRadiusLG }}>
          <Table
            dataSource={reporte?.filas || []}
            columns={columnasConIndice}
            rowKey={rowKey}
            pagination={{
              current: pagina.current,
              pageSize: pagina.pageSize,
              showSizeChanger: true,
              showTotal: (t) => `${t} registros`,
            }}
            onChange={(p) => setPagina({ current: p.current, pageSize: p.pageSize })}
            locale={{ emptyText: 'Sin datos registrados' }}
            // Los corrales agrupados llegan con `children`: abiertos de
            // entrada, que es lo que se vino a ver.
            expandable={{ defaultExpandAllRows: true }}
            // Los reportes anchos (ofertas por animal llegó a siete columnas)
            // scrollean en vez de apretar el nombre del animal hasta los
            // puntos suspensivos.
            scroll={{ x: 'max-content' }}
            summary={() => {
              const subs = reporte?.subtotales || {};
              // Sin subtotales la fila va igual, vacía y pintada, como cierre
              // de la tabla — mismo criterio que el Excel y el PDF. Hay
              // reportes donde ningún número se puede sumar honestamente.
              if (!reporte?.filas?.length) return null;
              // Es el total del reporte COMPLETO, no el de la página que se
              // está viendo — por eso `subtotales` viene del backend sobre
              // todas las filas y no se suma el `dataSource` de la página.
              // (Sin `fixed`: la tabla scrollea sólo en horizontal, y `fixed`
              // en el summary sirve para pegarlo al pie con `scroll.y`.)
              return (
                <Table.Summary>
                  <Table.Summary.Row style={{ background: token.colorFillAlter, fontWeight: 600 }}>
                    {columnasConIndice.map((col, i) => {
                      const key = col.dataIndex;
                      const valor = subs[key];
                      return (
                        <Table.Summary.Cell key={key || i} index={i} align={col.align}>
                          {valor !== undefined
                            ? (COLS_MONEDA.has(key)
                                ? `$${Number(valor).toLocaleString('es-AR')}`
                                : Number(valor).toLocaleString('es-AR'))
                            : (i === 0 && Object.keys(subs).length ? 'SUBTOTAL' : null)}
                        </Table.Summary.Cell>
                      );
                    })}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default AnalyticsCompleto;
